import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Radio,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Close } from "@mui/icons-material";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import { getUserData } from "../../../common/EntitiesManagement";
import { EvidenceTypeEnum } from "../../../common/utils";

const SIGNATURE_WIDTH = 150;
const SIGNATURE_HEIGHT = 80;

const STEP_LABELS = [
  localize.signature_step_welcome,
  localize.signature_step_type,
  localize.signature_step_draw,
  localize.signature_step_data,
];

export default function RegisterSignatureWizard({
  show,
  setShow,
  eventId,
  eventCode,
  onSaved,
}) {
  const { EVIDENCE_ENDPOINT, config } = useGlobalContext();

  const [activeStep, setActiveStep] = useState(0);
  const [signatureType, setSignatureType] = useState(null); // "client" | "personal"
  const [signerName, setSignerName] = useState("");
  const [hasDrawn, setHasDrawn] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef({ x: 0, y: 0 });
  const signatureDataUrlRef = useRef(null);

  const resetState = () => {
    setActiveStep(0);
    setSignatureType(null);
    setSignerName("");
    setHasDrawn(false);
    setIsSaving(false);
    setErrorMessage(null);
    signatureDataUrlRef.current = null;
  };

  useEffect(() => {
    if (!show) {
      resetState();
    }
  }, [show]);

  // Reset the drawing surface (white background) when entering the draw step.
  useEffect(() => {
    if (activeStep === 2) {
      requestAnimationFrame(() => clearCanvas());
    }
  }, [activeStep]);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111111";
    setHasDrawn(false);
  };

  const getCanvasPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = getCanvasPoint(e);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const point = getCanvasPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    if (!hasDrawn) {
      setHasDrawn(true);
    }
  };

  const handlePointerUp = (e) => {
    drawingRef.current = false;
    canvasRef.current?.releasePointerCapture?.(e.pointerId);
  };

  // Persist the drawn signature before the canvas unmounts (leaving the draw step).
  const captureSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      signatureDataUrlRef.current = canvas.toDataURL("image/png");
    }
  };

  const buildSignaturePng = () =>
    new Promise((resolve, reject) => {
      const dataUrl = signatureDataUrlRef.current;
      if (!dataUrl) {
        reject(new Error("No signature captured"));
        return;
      }
      const dest = document.createElement("canvas");
      dest.width = SIGNATURE_WIDTH;
      dest.height = SIGNATURE_HEIGHT;
      const ctx = dest.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);

      const image = new Image();
      image.onload = () => {
        ctx.drawImage(
          image,
          0,
          0,
          image.width,
          image.height,
          0,
          0,
          SIGNATURE_WIDTH,
          SIGNATURE_HEIGHT,
        );
        dest.toBlob((blob) => resolve(blob), "image/png");
      };
      image.onerror = () => reject(new Error("Could not load signature image"));
      image.src = dataUrl;
    });

  const uploadSignature = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    try {
      const blob = await buildSignaturePng();
      const evidenceTypeId =
        signatureType === "client"
          ? EvidenceTypeEnum.ClientSignature
          : EvidenceTypeEnum.PersonalSignature;
      const fileName = `${signatureType}_signature_${eventCode ?? eventId}.png`;
      const file = new File([blob], fileName, { type: "image/png" });

      const requestData = {
        Id: 0,
        Name: signerName,
        EvidenceTypeId: evidenceTypeId,
        EvidenceType: evidenceTypeId,
        Path: fileName,
        eventId: eventId,
        EventId: eventId,
        EventCode: eventCode,
        event_id: eventId,
        event_code: eventCode,
        CreatedBy: getUserData()?.UserName,
        GpsLongidute: 0,
        GpsLatitude: 0,
        Available: 1,
        FromWeb: true,
        AdditionalData: "",
      };

      const formData = new FormData();
      formData.append(
        "requestData",
        new Blob([JSON.stringify(requestData)], { type: "application/json" }),
      );
      formData.append("document", file, fileName);

      const requestConfig = {
        ...config,
        headers: { ...config.headers, "Content-Type": undefined },
      };

      const result = await axios.post(
        EVIDENCE_ENDPOINT,
        formData,
        requestConfig,
      );
      const data = result?.data;
      if (!data?.Ok && !data?.ok) {
        setErrorMessage(data?.Data || data?.data || localize.error_unexpected);
        return;
      }

      onSaved && onSaved();
      setShow(false);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.Data ??
          error?.response?.data ??
          error?.message ??
          localize.error_unexpected,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    setShow(false);
  };

  const renderWelcomeStep = () => (
    <Box className="flex flex-col gap-3 w-full">
      <Alert severity="info">
        <span className="text-xs" style={{ whiteSpace: "pre-line" }}>
          {localize.signature_welcome_message}
        </span>
      </Alert>
    </Box>
  );

  const renderTypeCard = (type, title, subtitle) => {
    const selected = signatureType === type;
    return (
      <Card
        variant="outlined"
        onClick={() => setSignatureType(type)}
        sx={{
          flex: 1,
          cursor: "pointer",
          borderRadius: "10px",
          borderColor: selected ? "var(--app-bg, #10366E)" : "#E0E0E0",
          borderWidth: selected ? "2px" : "1px",
          backgroundColor: selected ? "rgba(16,54,110,0.04)" : "transparent",
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Box className="flex items-center gap-1">
            <Radio
              size="small"
              checked={selected}
              onChange={() => setSignatureType(type)}
              value={type}
              sx={{ p: 0.5, color: "var(--app-bg, #10366E)", "&.Mui-checked": { color: "var(--app-bg, #10366E)" } }}
            />
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700 }}>
              {title}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.78rem", color: "#4B5563", mt: 1 }}>
            {subtitle}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  const renderTypeStep = () => (
    <Box className="flex flex-col sm:flex-row gap-3 w-full">
      {renderTypeCard(
        "client",
        localize.signature_type_client_title,
        localize.signature_type_client_subtitle,
      )}
      {renderTypeCard(
        "personal",
        localize.signature_type_personal_title,
        localize.signature_type_personal_subtitle,
      )}
    </Box>
  );

  const renderDrawStep = () => (
    <Box className="flex flex-col gap-2 w-full items-center">
      <Typography
        className="text-center"
        sx={{ fontSize: "0.95rem", fontWeight: 600 }}
      >
        {localize.signature_draw_title}
      </Typography>
      <canvas
        ref={canvasRef}
        width={450}
        height={240}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{
          width: "100%",
          maxWidth: "450px",
          height: "auto",
          aspectRatio: "450 / 240",
          border: "1px dashed #9CA3AF",
          borderRadius: "8px",
          backgroundColor: "#ffffff",
          touchAction: "none",
          cursor: "crosshair",
        }}
      />
      <Button
        size="small"
        variant="text"
        color="error"
        onClick={clearCanvas}
        style={{ fontSize: "0.8em" }}
      >
        {localize.signature_clear}
      </Button>
    </Box>
  );

  const renderDataStep = () => (
    <Box className="flex flex-col gap-3 w-full">
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
        {signatureType === "client"
          ? localize.signature_enter_client_name
          : localize.signature_enter_personal_name}
      </Typography>
      <TextField
        required
        fullWidth
        size="small"
        label={localize.name}
        variant="outlined"
        value={signerName}
        disabled={isSaving}
        onChange={(e) => setSignerName(e.target.value)}
        InputLabelProps={getInputLabelProps()}
        inputProps={getInputProps()}
      />
      {errorMessage && (
        <Alert severity="error">
          <span className="text-xs">{`${errorMessage}`}</span>
        </Alert>
      )}
    </Box>
  );

  const renderStepBody = () => {
    switch (activeStep) {
      case 0:
        return renderWelcomeStep();
      case 1:
        return renderTypeStep();
      case 2:
        return renderDrawStep();
      case 3:
        return renderDataStep();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    const primarySx = {
      backgroundColor: "var(--app-bg, #10366E)",
      color: "var(--app-fg, #ffffff)",
      "&:hover": { backgroundColor: "var(--app-bg, #10366E)" },
    };

    return (
      <>
        {activeStep > 0 && (
          <Button
            variant="text"
            color="inherit"
            disabled={isSaving}
            onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          >
            {localize.previous}
          </Button>
        )}

        {activeStep === 0 && (
          <Button
            variant="contained"
            sx={primarySx}
            onClick={() => setActiveStep(1)}
          >
            {localize.next}
          </Button>
        )}

        {activeStep === 1 && (
          <Button
            variant="contained"
            sx={primarySx}
            disabled={!signatureType}
            onClick={() => setActiveStep(2)}
          >
            {localize.next}
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            variant="contained"
            sx={primarySx}
            disabled={!hasDrawn}
            onClick={() => {
              captureSignature();
              setActiveStep(3);
            }}
          >
            {localize.continue}
          </Button>
        )}

        {activeStep === 3 && (
          <LoadingButton
            loading={isSaving}
            variant="contained"
            sx={primarySx}
            disabled={!signerName || signerName.trim().length === 0}
            onClick={uploadSignature}
          >
            {localize.save}
          </LoadingButton>
        )}
      </>
    );
  };

  return (
    <Dialog
      open={show}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return;
        handleClose();
      }}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <span>{localize.register_signatures}</span>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isSaving}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mb: 3,
            "& .MuiStepLabel-labelContainer": {
              display: { xs: "none", sm: "block" },
            },
            "& .MuiStepLabel-label": {
              fontSize: { sm: "0.75rem", md: "0.875rem" },
            },
            "& .MuiStepIcon-root.Mui-active": {
              color: "var(--app-bg, #10366E)",
            },
            "& .MuiStepIcon-root.Mui-completed": {
              color: "var(--app-bg, #10366E)",
            },
            "& .MuiStepIcon-text": {
              fill: "var(--app-fg, #ffffff)",
            },
          }}
        >
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStepBody()}
      </DialogContent>
      <Divider />
      <DialogActions>{renderFooter()}</DialogActions>
    </Dialog>
  );
}
